// One tool of a toolkit (02-DESIGN/03-console/01-the-console.md § the
// address; § conventions: the tabs of a toolkit's page, the moves of
// 2026-11-01; hq decision 0069): `toolkits/<toolkit>/tools/<tool>`, opened
// from the row on the toolkit's *Tools* tab. Its title is the tool's name,
// its crumb goes back to the toolkit, and it holds the tool's description
// whole — the page exists to show it — with the input it takes, as the
// toolkit advertises its schema, a summary first with *Show more*.

import { Link, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { Empty, NotAllowed, PageHead, Pending } from "../../ui/components.tsx";
import { MoreText } from "../../ui/more.tsx";
import { find, validName, type Toolkit } from "../../wire/toolkit.ts";
import { useSurface } from "../surface.tsx";
import { useToolkits } from "./useToolkits.ts";

/** SCHEMA_LINES is how much of a tool's input schema shows before *Show more*. */
const SCHEMA_LINES = 12;

/** schemaText is a tool's advertised schema, indented for reading where it parses, as it came where it does not. */
export function schemaText(schema: string | undefined): string | undefined {
  if (schema === undefined || schema.trim() === "") {
    return undefined;
  }
  try {
    return JSON.stringify(JSON.parse(schema), null, 2);
  } catch {
    return schema;
  }
}

export function ToolPage() {
  const { name = "", tool = "" } = useParams();
  const s = useSurface();
  const t = useToolkits();
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "toolkits")}>Toolkits</Link>
      <span>/</span>
      {validName(name) ? (
        <Link to={tenantPath(s.tenant, "toolkits", name)}>{name}</Link>
      ) : (
        <span>{name}</span>
      )}
      <span>/</span>
      <span>{tool}</span>
    </>
  );
  if (t.toolkits.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Looking for the tool" />
      </>
    );
  }
  if (t.toolkits.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title={tool} />
        <NotAllowed err={t.toolkits.err} what="This tool" />
      </>
    );
  }
  let tk: Toolkit | undefined;
  try {
    tk = validName(name) ? find(t.toolkits.value, name) : undefined;
  } catch {
    tk = undefined;
  }
  const x = tk?.tools.find((y) => y.name === tool);
  if (tk === undefined || x === undefined) {
    return (
      <>
        <PageHead crumb={crumb} title={tool} line={`Not a tool ${name} offers right now.`} />
        <Empty>A toolkit lists its tools once it has reached its server.</Empty>
      </>
    );
  }
  const schema = schemaText(x.schema);
  return (
    <>
      <PageHead crumb={crumb} title={x.name} line={`a tool of ${tk.name}`} />
      <section className="section">
        <p style={{ whiteSpace: "pre-wrap" }}>{x.description === "" ? "No description." : x.description}</p>
      </section>
      {schema !== undefined && (
        <section className="section">
          <div className="section-head">
            <h2>What it takes</h2>
          </div>
          <MoreText text={schema} lines={SCHEMA_LINES} />
        </section>
      )}
    </>
  );
}
